/**
 * Authenticated API utility for making HTTP requests with JWT tokens
 * Handles token refresh and fallback to mock data for development
 */

import { jwtAuthService } from '@/services/jwtAuthService';
import { endSuspendedSession, isSubscriptionInactive } from '@/services/subscriptionGate';

export interface ApiResponse<T = any> {
  data?: T;
  error?: string;
  status: number;
  usedMockData?: boolean;
}

export interface AuthenticatedRequestOptions extends RequestInit {
  baseURL?: string;
  timeout?: number;
  fallbackToMockData?: boolean;
  mockData?: any;
}

/**
 * Make an authenticated API request with automatic token refresh
 */
export async function authenticatedRequest<T = any>(
  url: string,
  options: AuthenticatedRequestOptions = {}
): Promise<ApiResponse<T>> {
  const {
    baseURL = '',
    timeout = 10000,
    fallbackToMockData = true,
    mockData,
    ...fetchOptions
  } = options;

  // Construct full URL
  const fullUrl = url.startsWith('http') ? url : `${baseURL}${url}`;

  try {
    // Get access token
    const token = await jwtAuthService.getAccessToken();
    
    console.log('🔑 authenticatedRequest:', {
      url: fullUrl,
      hasToken: !!token,
      tokenPreview: token ? token.substring(0, 50) + '...' : 'NO_TOKEN'
    });
    
    // Prepare headers
    const headers = new Headers(fetchOptions.headers);
    headers.set('Content-Type', 'application/json');
    
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
      console.log('✅ Authorization header set');
    } else {
      console.warn('❌ No token available for authenticated request');
    }

    // Create fetch promise with timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    const fetchPromise = fetch(fullUrl, {
      ...fetchOptions,
      headers,
      signal: controller.signal,
    });

    try {
      const response = await fetchPromise;
      clearTimeout(timeoutId);

      // 402 = this lab's LabBOXX subscription is suspended (entitlement gate):
      // end the session rather than render broken widgets or mock data.
      if (await isSubscriptionInactive(response)) {
        endSuspendedSession();
        return { error: 'subscription_inactive', status: 402 };
      }

      // Handle 401 - try to refresh token once
      if (response.status === 401 && token) {
        console.log('🔄 Got 401, attempting token refresh...');
        const refreshed = await jwtAuthService.attemptRefresh();
        
        if (refreshed) {
          const newToken = await jwtAuthService.getAccessToken();
          if (newToken) {
            headers.set('Authorization', `Bearer ${newToken}`);
            const retryResponse = await fetch(fullUrl, {
              ...fetchOptions,
              headers,
            });
            
            if (retryResponse.ok) {
              const data = await retryResponse.json();
              return {
                data,
                status: retryResponse.status,
              };
            }
          }
        }
      }

      // Handle successful response
      if (response.ok) {
        const data = await response.json();
        return {
          data,
          status: response.status,
        };
      }

      // Handle error response
      const errorText = await response.text();
      return {
        error: errorText || `HTTP ${response.status}`,
        status: response.status,
      };

    } catch (fetchError) {
      clearTimeout(timeoutId);
      throw fetchError;
    }

  } catch (error) {
    console.warn(`API request failed for ${fullUrl}:`, error);
    
    // Fallback to mock data if enabled
    if (fallbackToMockData && mockData) {
      console.warn(`Using mock data for ${url}`);
      return {
        data: mockData,
        status: 200,
        usedMockData: true,
      };
    }

    return {
      error: error instanceof Error ? error.message : 'Network error',
      status: 0,
    };
  }
}

/**
 * Convenience functions for common HTTP methods
 */
export const authenticatedApi = {
  get: <T = any>(url: string, options?: AuthenticatedRequestOptions) =>
    authenticatedRequest<T>(url, { ...options, method: 'GET' }),

  post: <T = any>(url: string, data?: any, options?: AuthenticatedRequestOptions) =>
    authenticatedRequest<T>(url, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }),

  put: <T = any>(url: string, data?: any, options?: AuthenticatedRequestOptions) =>
    authenticatedRequest<T>(url, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    }),

  delete: <T = any>(url: string, options?: AuthenticatedRequestOptions) =>
    authenticatedRequest<T>(url, { ...options, method: 'DELETE' }),

  patch: <T = any>(url: string, data?: any, options?: AuthenticatedRequestOptions) =>
    authenticatedRequest<T>(url, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    }),
};

export default authenticatedApi;